import { useEffect, useRef, useState } from 'react';

import {
  getUserTraits,
  TRELLO_JIRA_CONF_CO_USERS,
  useIsOptimizedUserTraitsEnabled,
  useUserTrait,
} from '@trello/atlassian-personalization';
import { useAaId, useMemberId } from '@trello/authentication';
import { getDateBefore, idToDate } from '@trello/dates';

import { getAccessibleProducts } from '../../getAccessibleProducts';
import type { RuntimeEligibilityCheckResult } from './RuntimeEligibilityCheck';
import { useIsCrossFlowMemberConfirmed } from './useIsCrossFlowMemberConfirmed';

export const useHasNoAvailableSites = ({
  doLoadAvailableSites,
}: {
  doLoadAvailableSites: boolean;
}): RuntimeEligibilityCheckResult => {
  const isLoadingAvailableSites = useRef(false);

  const [hasNoAvailableSites, setHasNoAvailableSites] = useState(false);

  const { isEligible: isMemberConfirmed } = useIsCrossFlowMemberConfirmed();
  const memberId = useMemberId();
  const aaId = useAaId();
  const isNewUser = idToDate(memberId) > getDateBefore({ days: 2 });

  const isOptimizedUserTraitsEnabled = useIsOptimizedUserTraitsEnabled();
  const { value: isJiraConfCoUserTrait, loading } = useUserTrait(
    TRELLO_JIRA_CONF_CO_USERS,
  );

  useEffect(() => {
    const getCoUserTrait = async () => {
      isLoadingAvailableSites.current = true;
      if (!aaId) {
        setHasNoAvailableSites(true);
        isLoadingAvailableSites.current = false;
        return;
      }
      if (isOptimizedUserTraitsEnabled) {
        if (!loading) {
          setHasNoAvailableSites(!isJiraConfCoUserTrait);
          isLoadingAvailableSites.current = false;
        }
      } else {
        try {
          const traits = await getUserTraits(aaId);
          const coUserTrait = traits?.[TRELLO_JIRA_CONF_CO_USERS]
            ? Boolean(traits[TRELLO_JIRA_CONF_CO_USERS])
            : false;
          setHasNoAvailableSites(!coUserTrait);
        } finally {
          isLoadingAvailableSites.current = false;
        }
      }
    };

    const loadSiteEligibility = async () => {
      try {
        isLoadingAvailableSites.current = true;
        const { products } = await getAccessibleProducts();
        const hasNoSites =
          products?.length > 0 &&
          products?.filter(({ productId }) => productId !== 'trello').length ===
            0;
        setHasNoAvailableSites(hasNoSites);
      } finally {
        isLoadingAvailableSites.current = false;
      }
    };

    // if the user isn't confirmed, they won't have any sites
    if (!isMemberConfirmed) {
      setHasNoAvailableSites(true);
      return;
    }
    if (doLoadAvailableSites && !isLoadingAvailableSites.current) {
      if (!isNewUser) {
        getCoUserTrait();
      } else {
        loadSiteEligibility();
      }
    }
  }, [
    doLoadAvailableSites,
    isMemberConfirmed,
    isNewUser,
    aaId,
    memberId,
    isJiraConfCoUserTrait,
    isOptimizedUserTraitsEnabled,
    loading,
  ]);

  return {
    isEligible: hasNoAvailableSites,
  };
};
